import { useEffect, useState } from 'react';
import axios from 'src/utils/axios';
import { FederatedProvider, isFederatedProvider } from './federatedAuth';

const PROVIDERS_URL = 'auth/federated/providers/';

// Degradation fallback used only when the endpoint is unreachable or returns
// something malformed: preserve the pre-ENG-3554 Google button rather than
// dropping every provider on a transient error. A well-formed empty array is
// honoured (the backend deliberately enables none).
const FALLBACK_PROVIDERS: FederatedProvider[] = ['google'];

let cache: FederatedProvider[] | null = null;
let inflight: Promise<FederatedProvider[]> | null = null;

/** Test seam: drop the module-level cache between tests. */
export const resetFederatedProvidersCache = (): void => {
  cache = null;
  inflight = null;
};

const sanitize = (value: unknown): FederatedProvider[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }
  return value.filter(isFederatedProvider) as FederatedProvider[];
};

export const loadFederatedProviders = (): Promise<FederatedProvider[]> => {
  if (cache) {
    return Promise.resolve(cache);
  }
  if (!inflight) {
    inflight = Promise.resolve(axios.get(PROVIDERS_URL))
      .then((response) => {
        const providers = sanitize(
          (response as { data?: { providers?: unknown } } | undefined)?.data
            ?.providers
        );
        cache = providers ?? FALLBACK_PROVIDERS;
        return cache;
      })
      .catch(() => {
        cache = FALLBACK_PROVIDERS;
        return cache;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
};

/**
 * Enabled consumer providers, fetched once from the backend so the UI renders
 * exactly what the backend will accept (ENG-3390).
 */
export const useFederatedProviders = (): FederatedProvider[] => {
  const [providers, setProviders] = useState<FederatedProvider[]>(
    cache ?? FALLBACK_PROVIDERS
  );

  useEffect(() => {
    let active = true;
    void loadFederatedProviders().then((next) => {
      if (active) {
        setProviders(next);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  return providers;
};
